import { Link } from 'react-router-dom';

import { TopStatusBar } from '../components/domain/TopStatusBar';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAccounts } from '../features/accounts/queries';
import { useMe } from '../features/auth/queries';
import { useWeeklyReviews } from '../features/reviews/queries';
import { useLatestSnapshot } from '../features/snapshots/queries';
import { formatCurrency } from '../lib/format/currency';

export function WeeklyReviews() {
  const me = useMe();
  const accounts = useAccounts();
  const activeAccountId = accounts.data?.[0]?.id ?? null;
  const snapshot = useLatestSnapshot(activeAccountId);
  const reviews = useWeeklyReviews();
  const latest = reviews.data?.[0] ?? null;
  const older = reviews.data?.slice(1) ?? [];

  if (me.isLoading || reviews.isLoading) {
    return <main className="p-6 text-text-muted">Loading weekly reviews...</main>;
  }
  if (me.isError || reviews.isError || !me.data) {
    return <main className="p-6 text-danger">Could not load weekly reviews.</main>;
  }

  return (
    <main className="min-h-screen bg-bg text-text">
      <TopStatusBar currency={me.data.baseCurrency} snapshot={snapshot.data} />
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Weekly reviews</h1>
            <p className="mt-1 text-sm text-text-muted">Latest review stays pinned.</p>
          </div>
          <Button asChild variant="secondary">
            <Link to="/dashboard">Dashboard</Link>
          </Button>
        </header>

        {latest ? (
          <Card className="border-accent">
            <ReviewRow review={latest} currency={me.data.baseCurrency} pinned />
          </Card>
        ) : (
          <Card>
            <p className="text-sm text-text-muted">
              No weekly review has been generated yet. Run regenerate for a week from the API or
              wait for the scheduled Sunday job.
            </p>
          </Card>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {older.map((review) => (
            <Card key={review.id}>
              <ReviewRow review={review} currency={me.data.baseCurrency} />
            </Card>
          ))}
        </div>
      </div>
    </main>
  );
}

function ReviewRow({
  currency,
  pinned = false,
  review,
}: {
  currency: string;
  pinned?: boolean;
  review: NonNullable<ReturnType<typeof useWeeklyReviews>['data']>[number];
}) {
  const metrics = review.metrics;
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-accent text-xs font-semibold uppercase tracking-wide">
            {pinned ? 'Latest' : 'Review'}
          </div>
          <h2 className="text-xl font-semibold">Week of {review.weekStart}</h2>
          <p className="text-sm text-text-muted">
            Generated {new Date(review.generatedAt).toLocaleString()}
          </p>
        </div>
        <Button asChild>
          <Link to={`/reviews/weekly/${review.weekStart}`}>Open</Link>
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Metric label="Realised" value={formatCurrency(metrics.headline.realisedPnl, currency)} />
        <Metric
          label="Win rate"
          value={metrics.winRate === null ? '-' : `${(metrics.winRate * 100).toFixed(0)}%`}
        />
        <Metric label="Flags" value={String(review.flagsSummary.total)} />
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-border bg-bg-muted p-3">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="mt-1 font-semibold tabular-nums">{value}</div>
    </div>
  );
}
